// src/components/OptimizedStarryBackground.tsx
import React, { useEffect, useRef } from "react";

export const OptimizedStarryBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Referens för att hålla reda på rotationsvinkeln
  const rotationRef = useRef(0);
  // Flagga för att säkerställa att endast ett shooting star schemaläggs åt gången
  const shootingStarScheduled = useRef(false);
  // Flagga för att kontrollera om en satellit schemaläggs
  const satelliteScheduled = useRef(false);
  // Referens för att hålla reda på animationsframe
  const animationFrameRef = useRef<number | null>(null);
  // Center-position för rotation
  const centerRef = useRef({ x: 0, y: 0 });
  // Mobildetektering
  const isMobileRef = useRef(window.innerWidth < 768);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Funktion för att sätta canvasens storlek utifrån window.innerWidth/Height och devicePixelRatio
    const setCanvasSize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      
      // Uppdatera centrum
      centerRef.current = {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      };
      
      // Uppdatera mobildetektering
      isMobileRef.current = window.innerWidth < 768;
    };
    setCanvasSize();

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const safeCtx = ctx;
    safeCtx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);

    // --- Typer och variabler ---
    interface Star {
      // Använd polar koordinatsystem för att göra rotation enklare
      distance: number;     // Avstånd från centrum
      angle: number;        // Vinkel i radianer
      radius: number;       // Stjärnans radie
      opacity: number;      // Aktuell opacitet
      speed: number;        // Blinkningshastighet
      maxOpacity: number;   // Maximal opacitet
      direction: number;    // Riktning för blinkning
      twinkleSpeed: number; // Hastighet för blinkning
      pulseAmplitude: number; // Amplitud för pulsering
      pulseFrequency: number; // Frekvens för pulsering
      pulsePhase: number;     // Fasförskjutning för pulsering
    }
    
    interface ShootingStar {
      x: number;
      y: number;
      length: number;
      speed: number;
      angle: number;
      opacity: number;
      active: boolean;
      trail: { x: number; y: number }[];
      trailLength: number;
      lifetime: number;
      age: number;
    }
    
    interface Satellite {
      x: number;
      y: number;
      speed: number;
      angle: number;
      curveFactor?: number; // Faktor för att simulera omloppsbana
      size: number;
      opacity: number;
      active: boolean;
      lifetime: number;
      age: number;
      blink?: boolean;
      blinkFrequency?: number;
      blinkColor?: string;
      trackTime?: number;
    }
    
    const stars: Star[] = [];
    const shootingStars: ShootingStar[] = [];
    const satellites: Satellite[] = [];
    let particleTime = 0;

    // --- Funktioner ---
    function createStars() {
      stars.length = 0;
      
      // Reducera antalet stjärnor på mobil för bättre prestanda
      const count = isMobileRef.current 
        ? Math.min(window.innerWidth / 4, 150) 
        : Math.min(window.innerWidth / 3, 300);
      
      for (let i = 0; i < count; i++) {
        // Använd polära koordinater för att göra rotation enklare
        const distance = Math.random() * Math.min(window.innerWidth, window.innerHeight) * 0.8;
        const angle = Math.random() * 2 * Math.PI;
        
        // Mindre och mer dämpade stjärnor på mobil för bättre prestanda
        const mobileScaleFactor = isMobileRef.current ? 0.7 : 1;
        
        stars.push({
          distance: distance,
          angle: angle,
          radius: (Math.random() * 1.5 + 0.1) * mobileScaleFactor,
          opacity: Math.random() * mobileScaleFactor,
          maxOpacity: (0.5 + Math.random() * 0.5) * mobileScaleFactor,
          speed: (0.001 + Math.random() * 0.005) * mobileScaleFactor,
          direction: Math.random() > 0.5 ? 1 : -1,
          twinkleSpeed: (0.001 + Math.random() * 0.01) * mobileScaleFactor,
          pulseAmplitude: (0.1 + Math.random() * 0.2) * mobileScaleFactor,
          pulseFrequency: 0.003 + Math.random() * 0.004,
          pulsePhase: Math.random() * 2 * Math.PI,
        });
      }
    }

    function scheduleNextShootingStar() {
      if (shootingStarScheduled.current) return;
      shootingStarScheduled.current = true;
      
      // Längre väntetid mellan stjärnfall på mobil
      const delayFactor = isMobileRef.current ? 1.5 : 1;
      // Öka väntetiden mellan stjärnfall för färre stjärnfall
      const delay = (10000 + Math.random() * 15000) * delayFactor; // 10-25 sekunder (längre intervall)
      
      setTimeout(() => {
        createNewShootingStar(0);
        shootingStarScheduled.current = false;
      }, delay);
    }

    function createNewShootingStar(delay = 0) {
      // Gör stjärnfall mer diskreta genom att minska dess visuella påverkan
      const angle = Math.random() * (Math.PI / 4) + Math.PI / 4;
      
      // Reducera komplexitet på mobil för bättre prestanda
      const mobileFactor = isMobileRef.current ? 0.7 : 1;
      
      shootingStars.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight * 0.3,
        length: (20 + Math.random() * 30) * mobileFactor, // Minska längden
        speed: (2 + Math.random() * 4) * mobileFactor,   // Minska hastigheten
        angle: angle,
        opacity: 0,
        active: false,
        trail: [],
        trailLength: (5 + Math.random() * 8) * mobileFactor, // Minska traillängden
        lifetime: 800 + Math.random() * 1200, // Kortare livstid
        age: -delay,
      });
    }
    
    function scheduleNextSatellite() {
      if (satelliteScheduled.current) return;
      satelliteScheduled.current = true;
      
      // Färre satelliter på mobil
      const delayFactor = isMobileRef.current ? 1.5 : 1;
      // Kortare tid mellan satelliter för att öka sannolikheten att se dem
      const delay = (8000 + Math.random() * 12000) * delayFactor; // 8-20 sekunder
      
      setTimeout(() => {
        // Färre satelliter i formation på mobil
        const maxSatellites = isMobileRef.current ? 2 : 3;
        // Skapa ibland flera satelliter i formation
        const satelliteCount = Math.random() < 0.3 ? Math.floor(Math.random() * maxSatellites) + 1 : 1;
        
        for (let i = 0; i < satelliteCount; i++) {
          // Fördröj varje satellit i formationen något
          setTimeout(() => {
            createNewSatellite();
          }, i * 1000); // 1 sekund mellan varje satellit i formation
        }
        
        satelliteScheduled.current = false;
      }, delay);
    }

    function createNewSatellite() {
      // Skapa en banelliptisk bana runt jorden
      // Använd slumpmässiga ingångslägen men följ sedan en krökt bana
      
      // Välj en startpunkt i skärmens kant
      const startSide = Math.floor(Math.random() * 4); // 0: topp, 1: höger, 2: botten, 3: vänster
      
      let startX, startY;
      let trajectoryAngle; // Initial riktningsvinkel
      
      // Bestäm startposition baserat på vilken sida satelliten kommer in från
      switch(startSide) {
        case 0: // Toppen
          startX = Math.random() * window.innerWidth;
          startY = -5;
          trajectoryAngle = Math.PI/2 + (Math.random() * 0.6 - 0.3); // Nedåt med variationer
          break;
        case 1: // Höger
          startX = window.innerWidth + 5;
          startY = Math.random() * window.innerHeight;
          trajectoryAngle = Math.PI + (Math.random() * 0.6 - 0.3); // Vänster med variationer
          break;
        case 2: // Botten
          startX = Math.random() * window.innerWidth;
          startY = window.innerHeight + 5;
          trajectoryAngle = Math.PI * 1.5 + (Math.random() * 0.6 - 0.3); // Uppåt med variationer
          break;
        case 3: // Vänster
        default:
          startX = -5;
          startY = Math.random() * window.innerHeight;
          trajectoryAngle = 0 + (Math.random() * 0.6 - 0.3); // Höger med variationer
          break;
      }
      
      // De flesta satelliter ska blinka
      const hasBlink = Math.random() > 0.2; // 80% chans att den blinkar
      
      // Slumpmässig blinkfärg
      let blinkColor = 'white'; // Standard är vit
      
      if (hasBlink && Math.random() > 0.7) {
        // 30% av blinkande satelliter har färgat ljus
        if (Math.random() > 0.5) {
          blinkColor = 'red'; // Rött navigationsljus
        } else {
          blinkColor = 'blue'; // Blått navigationsljus
        }
      }
      
      // Mindre satellitpunkt på mobil
      const mobileFactor = isMobileRef.current ? 0.6 : 1;
      // Extremt liten satellit
      const satelliteSize = (0.3 + Math.random() * 0.4) * mobileFactor; // 0.3-0.7 pixel
      
      satellites.push({
        x: startX,
        y: startY,
        speed: 0.1 + Math.random() * 0.3, // Extremt långsam rörelse
        angle: trajectoryAngle,
        curveFactor: (Math.random() * 0.02 - 0.01), // Liten banjustering för att simulera omloppsbana
        size: satelliteSize,
        opacity: 0,
        active: true,
        lifetime: (Math.max(window.innerWidth, window.innerHeight) * 5) / (0.1 + Math.random() * 0.3), // Avsevärt längre livstid (5x istället för 3x)
        age: 0,
        blink: hasBlink,
        blinkFrequency: hasBlink ? 0.005 + Math.random() * 0.015 : 0, // Betydligt snabbare blinkfrekvens
        blinkColor: blinkColor,
        trackTime: 0 // Tidsspårning för banjustering
      });
    }

    function drawStarryBackground(time: number) {
      // Avbryt tidigare animation om den finns
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      safeCtx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      // Huvudbakgrundsgradient - mörkare för mobil för bättre kontrast
      const gradientTop = isMobileRef.current ? "rgba(8, 12, 28, 1)" : "rgba(10, 15, 35, 1)";
      const gradientBottom = isMobileRef.current ? "rgba(5, 8, 20, 1)" : "rgba(8, 12, 28, 1)";
      
      const gradient = safeCtx.createLinearGradient(0, 0, 0, window.innerHeight);
      gradient.addColorStop(0, gradientTop);     // Mörkare blåsvart topp
      gradient.addColorStop(1, gradientBottom);  // Ännu mörkare botten
      safeCtx.fillStyle = gradient;
      safeCtx.fillRect(0, 0, window.innerWidth, window.innerHeight);
      
      // Extra mörk gradient i vänstra nedre hörnet för djupeffekt
      const cornerGradient = safeCtx.createRadialGradient(
        0, window.innerHeight,  // centrum i vänstra nedre hörnet
        0,                      // startradius
        0, window.innerHeight,  // samma centrum
        window.innerWidth * 0.5 // slut vid ca halva bredden
      );
      cornerGradient.addColorStop(0, "rgba(5, 8, 18, 0.7)");   // Mörkare ton
      cornerGradient.addColorStop(1, "rgba(5, 8, 18, 0)");     // Transparent utåt
      safeCtx.fillStyle = cornerGradient;
      safeCtx.fillRect(0, 0, window.innerWidth, window.innerHeight);

      // Uppdatera rotationen (extremt sakta)
      rotationRef.current += 0.00005; // Öka eller minska för att justera rotationshastighet

      // Reducera komplexitet på mobil
      if (!isMobileRef.current) {
        drawNebulae(time);
      }
      
      drawStars(time);
      
      // Minska antalet animerade objekt på mobil för att förbättra prestanda
      if (!isMobileRef.current || Math.random() > 0.5) {
        drawShootingStars(time);
      }
      
      if (!isMobileRef.current || Math.random() > 0.7) {
        drawSatellites(time);
      }

      particleTime = time;
      animationFrameRef.current = requestAnimationFrame(drawStarryBackground);
    }

    function drawNebulae(time: number) {
      const nebulaColors = [
        { color: "rgba(30, 40, 80, 0.03)", distance: window.innerWidth * 0.3, angle: rotationRef.current * 0.2, size: window.innerWidth * 0.5 },
        { color: "rgba(25, 35, 70, 0.03)", distance: window.innerWidth * 0.25, angle: rotationRef.current * 0.15 + 2, size: window.innerWidth * 0.4 },
        { color: "rgba(35, 45, 85, 0.03)", distance: window.innerWidth * 0.2, angle: rotationRef.current * 0.1 + 4, size: window.innerWidth * 0.3 },
      ];
      
      nebulaColors.forEach((nebula) => {
        const pulse = Math.sin(time * 0.0003) * 0.2 + 0.8;
        
        // Beräkna position baserat på rotationen
        const x = centerRef.current.x + Math.cos(nebula.angle) * nebula.distance;
        const y = centerRef.current.y + Math.sin(nebula.angle) * nebula.distance;
        
        safeCtx.save();
        safeCtx.globalAlpha = 0.6;
        safeCtx.beginPath();
        const grd = safeCtx.createRadialGradient(
          x,
          y,
          0,
          x,
          y,
          nebula.size * pulse
        );
        grd.addColorStop(0, nebula.color);
        grd.addColorStop(1, "rgba(0, 0, 0, 0)");
        safeCtx.fillStyle = grd;
        safeCtx.arc(x, y, nebula.size * pulse, 0, Math.PI * 2);
        safeCtx.fill();
        safeCtx.restore();
      });
    }

    function drawStars(time: number) {
      stars.forEach((star) => {
        // Uppdatera blinkning
        star.opacity += star.speed * star.direction * Math.sin(time * star.twinkleSpeed);
        if (star.opacity <= 0.1 || star.opacity >= star.maxOpacity) {
          star.direction *= -1;
        }
        star.opacity = Math.max(0.1, Math.min(star.maxOpacity, star.opacity));

        // Räkna ut stjärnans position med rotationen
        const rotatedAngle = star.angle + rotationRef.current;
        const x = centerRef.current.x + Math.cos(rotatedAngle) * star.distance;
        const y = centerRef.current.y + Math.sin(rotatedAngle) * star.distance;

        const pulsingRadius =
          star.radius * (1 + star.pulseAmplitude * Math.sin(time * star.pulseFrequency + star.pulsePhase));

        safeCtx.save();
        safeCtx.beginPath();
        
        // Optimera rendering på mobil - enklare gradient för bättre prestanda
        if (isMobileRef.current) {
          safeCtx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
          safeCtx.arc(x, y, pulsingRadius, 0, Math.PI * 2);
          safeCtx.fill();
        } else {
          // Mer avancerad gradient på desktop
          const gradient = safeCtx.createRadialGradient(
            x,
            y,
            0,
            x,
            y,
            pulsingRadius * 2
          );
          gradient.addColorStop(0, `rgba(255, 255, 255, ${star.opacity})`);
          gradient.addColorStop(0.5, `rgba(200, 220, 255, ${star.opacity * 0.6})`);
          gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
          safeCtx.fillStyle = gradient;
          safeCtx.arc(x, y, pulsingRadius, 0, Math.PI * 2);
          safeCtx.fill();
        }
        
        safeCtx.restore();
      });
    }

    function drawShootingStars(time: number) {
      if (shootingStars.length === 0) {
        scheduleNextShootingStar();
      }
      
      // Gå igenom stjärnfall i omvänd ordning för att kunna ta bort dem säkert
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const star = shootingStars[i];
        star.age += time - particleTime;
        
        if (star.age < 0) continue;
        if (!star.active && star.age >= 0) {
          star.active = true;
        }
        
        if (star.active) {
          const dx = Math.cos(star.angle) * star.speed;
          const dy = Math.sin(star.angle) * star.speed;
          star.x += dx;
          star.y += dy;
          
          star.trail.unshift({ x: star.x, y: star.y });
          if (star.trail.length > star.trailLength) {
            star.trail.pop();
          }
          
          if (star.age < 200) {
            star.opacity = Math.min(1, star.age / 200);
          } else if (star.age > star.lifetime - 200) {
            star.opacity = Math.max(0, (star.lifetime - star.age) / 200);
          } else {
            star.opacity = 1;
          }
          
          // Minska maximal opacitet för stjärnfallen för ett mer diskret utseende
          const maxOpacity = isMobileRef.current ? 0.4 : 0.5; // Minskat från 1.0
          const adjustedOpacity = star.opacity * maxOpacity;
          
          if (adjustedOpacity > 0) {
            safeCtx.save();
            if (star.trail.length >= 2) {
              safeCtx.beginPath();
              safeCtx.moveTo(star.trail[0].x, star.trail[0].y);
              for (let j = 1; j < star.trail.length; j++) {
                safeCtx.lineTo(star.trail[j].x, star.trail[j].y);
              }
              
              const gradient = safeCtx.createLinearGradient(
                star.trail[0].x,
                star.trail[0].y,
                star.trail[star.trail.length - 1].x,
                star.trail[star.trail.length - 1].y
              );
              
              gradient.addColorStop(0, `rgba(255, 255, 255, ${adjustedOpacity})`);
              gradient.addColorStop(0.3, `rgba(200, 220, 255, ${adjustedOpacity * 0.6})`);
              gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
              
              safeCtx.strokeStyle = gradient;
              safeCtx.lineWidth = isMobileRef.current ? 0.8 : 1.0; // Minska linjebredden på mobil
              safeCtx.stroke();
              
              safeCtx.beginPath();
              safeCtx.arc(star.trail[0].x, star.trail[0].y, isMobileRef.current ? 1.0 : 1.5, 0, Math.PI * 2); // Minska huvudet på mobil
              safeCtx.fillStyle = `rgba(255, 255, 255, ${adjustedOpacity})`;
              safeCtx.fill();
            }
            safeCtx.restore();
          }
          
          if (
            star.age > star.lifetime ||
            star.x < 0 ||
            star.x > window.innerWidth ||
            star.y < 0 ||
            star.y > window.innerHeight
          ) {
            shootingStars.splice(i, 1);
            scheduleNextShootingStar();
          }
        }
      }
    }
    
    function drawSatellites(time: number) {
      if (satellites.length === 0) {
        scheduleNextSatellite();
        return;
      }
      
      // Loopa genom satelliter baklänges för att kunna ta bort säkert
      for (let i = satellites.length - 1; i >= 0; i--) {
        const satellite = satellites[i];
        satellite.age += time - particleTime;
        
        // Uppdatera trackTime
        if (satellite.trackTime !== undefined) {
          satellite.trackTime += (time - particleTime);
        }
        
        // Uppdatera position med svagt krökt bana för att simulera omloppsbana
        let dx = Math.cos(satellite.angle) * satellite.speed;
        let dy = Math.sin(satellite.angle) * satellite.speed;
        
        // Lägg till en liten krökning i banan
        if (satellite.curveFactor && satellite.trackTime) {
          // Justera vinkeln något baserat på tiden
          satellite.angle += satellite.curveFactor * Math.sin(satellite.trackTime * 0.0005);
        }
        
        satellite.x += dx;
        satellite.y += dy;
        
        // Uppdatera opacitet med fade in/out
        let baseOpacity = isMobileRef.current ? 0.6 : 0.7; // Minska maximal opacitet för mer diskret effekt på mobil
        if (satellite.age < 1000) {
          baseOpacity = Math.min(baseOpacity, satellite.age / 1000);
        } else if (satellite.age > satellite.lifetime - 1000) {
          baseOpacity = Math.max(0, baseOpacity * (satellite.lifetime - satellite.age) / 1000);
        }
        
        // Lägg till blinkeffekt om satelliten har det
        let finalOpacity = baseOpacity;
        let fillColor = 'white';
        
        if (satellite.blink && satellite.blinkFrequency) {
          // Periodisk blinkfunktion, mer uttolad för navigationsljus
          const blinkPhase = time * satellite.blinkFrequency;
          const blinkFactor = Math.pow(Math.abs(Math.sin(blinkPhase)), 3); // Skarpare blinkning
          
          if (satellite.blinkColor && satellite.blinkColor !== 'white') {
            // Byt färg baserat på blink-fasen
            if (Math.floor(blinkPhase / Math.PI) % 2 === 0) {
              fillColor = satellite.blinkColor; // Använd specialfärg i en fas
            }
          }
          
          finalOpacity = baseOpacity * blinkFactor;
        }
        
        satellite.opacity = finalOpacity;
        
        // Rita satelliten (mycket liten prick)
        if (satellite.opacity > 0) {
          safeCtx.save();
          safeCtx.beginPath();
          safeCtx.arc(satellite.x, satellite.y, satellite.size, 0, Math.PI * 2);
          
          // Använd olika färger för olika navigationsljus
          if (fillColor === 'red') {
            safeCtx.fillStyle = `rgba(255, 50, 50, ${satellite.opacity})`; // Rött ljus
          } else if (fillColor === 'blue') {
            safeCtx.fillStyle = `rgba(50, 100, 255, ${satellite.opacity})`; // Blått ljus
          } else {
            safeCtx.fillStyle = `rgba(255, 255, 255, ${satellite.opacity})`; // Vitt ljus
          }
          
          safeCtx.fill();
          safeCtx.restore();
        }
        
        // Ta bort satelliten endast när den fullföljt sin omloppsbana 
        // och lämnat skärmen, inte i mitten av sin bana
        const isPastLifetime = satellite.age > satellite.lifetime;
        const isOffscreen = 
          satellite.x < -50 || 
          satellite.x > window.innerWidth + 50 || 
          satellite.y < -50 || 
          satellite.y > window.innerHeight + 50;
        
        // Ta bara bort när satelliten har en viss ålder OCH är utanför skärmen
        // Detta förhindrar att den försvinner mitt i sin synliga bana
        if (isPastLifetime && isOffscreen) {
          satellites.splice(i, 1);
          scheduleNextSatellite();
        }
      }
    }

    // Funktion för att hantera både resize och fullscreenändringar
    const handleResizeOrFullscreen = () => {
      setCanvasSize();
      createStars();
      if (shootingStars.length === 0) {
        scheduleNextShootingStar();
      }
      if (satellites.length === 0) {
        scheduleNextSatellite();
      }
    };

    // Lägg till eventlyssnare för window-resize och fullscreenchange
    window.addEventListener("resize", handleResizeOrFullscreen);
    document.addEventListener("fullscreenchange", handleResizeOrFullscreen);

    // Initiera stjärnor och starta animationen
    createStars();
    scheduleNextShootingStar();
    scheduleNextSatellite();
    animationFrameRef.current = requestAnimationFrame(drawStarryBackground);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener("resize", handleResizeOrFullscreen);
      document.removeEventListener("fullscreenchange", handleResizeOrFullscreen);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 -z-10"
      style={{
        width: "100%",
        height: "100%",
        background: "linear-gradient(to bottom, rgb(8, 12, 28), rgb(5, 8, 20))",
      }}
    />
  );
};

export default OptimizedStarryBackground;
